import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import DramaCard from '@/components/feature/DramaCard';
import HeroCarousel from './components/HeroCarousel';
import CategoryQuickLinks from './components/CategoryQuickLinks';
import LatestUpdates from './components/LatestUpdates';
import { dramas } from '@/mocks/dramas';
import { heroes } from '@/mocks/heroes';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function Home() {
  useDocumentMeta({
    title: '香草在线观看免费播放电视剧-高清电视剧在线播放平台',
    description: '香草在线观看免费播放电视剧提供最新高清电视剧资源，涵盖多种类型，支持在线观看，满足用户追剧需求。',
    keywords: '香草在线观看,免费播放电视剧,电视剧在线观看,香草电视剧,电视剧播放,电视剧下载,高清电视剧,在线观看平台',
  });

  const slides = heroes
    .map((hero) => {
      const drama = dramas.find((item) => item.id === hero.dramaId);
      return drama ? { drama, slogan: hero.slogan } : null;
    })
    .filter((slide): slide is { drama: (typeof dramas)[number]; slogan: string } => slide !== null);

  const hotList = [...dramas].sort((a, b) => b.hot - a.hot).slice(0, 8);
  const latestList = [...dramas]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 6);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <HeroCarousel slides={slides} />
        <CategoryQuickLinks />

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 md:mb-8">
            <div>
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-950">当季热门推荐</h2>
              <p className="mt-2 text-sm text-foreground-500">本季度最受欢迎的电视剧，追剧不迷路</p>
            </div>
            <Link to="/category" className="text-sm text-primary-700 hover:text-primary-600 font-medium inline-flex items-center gap-1 whitespace-nowrap">
              更多热门
              <i className="ri-arrow-right-s-line" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {hotList.map((drama, index) => (
              <DramaCard key={drama.id} drama={drama} index={index} />
            ))}
          </div>
        </section>

        <LatestUpdates items={latestList} />

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-950">
                关于香草在线观看免费播放电视剧
              </h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
                香草视频是一个面向追剧爱好者的电视剧内容聚合平台，专注为你汇总最新、最热的高清电视剧资源。
                我们按都市、古装、悬疑、爱情、科幻等类型细致分类，支持关键词搜索、多条件筛选与收藏关注，
                帮助你在海量剧集中快速找到想看的内容。无论是想追一部热播新剧，还是重温经典老剧，
                都能在香草视频获得清晰顺畅的浏览体验。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap"
                >
                  <i className="ri-apps-2-line" />
                  开始找剧
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50 border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-300 transition-colors whitespace-nowrap"
                >
                  <i className="ri-information-line" />
                  了解更多
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 lg:grid-cols-1 gap-4">
              <StatItem value={`${dramas.length}+`} label="在库电视剧" icon="ri-film-line" />
              <StatItem value="9" label="内容类型" icon="ri-price-tag-3-line" />
              <StatItem value="100%" label="免费在线观看" icon="ri-play-circle-line" />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function StatItem({ value, label, icon }: { value: string; label: string; icon: string }) {
  return (
    <div className="rounded-lg bg-background-50 border border-primary-100 p-4 flex flex-col justify-center">
      <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-2">
        <i className={icon} />
      </span>
      <span className="font-heading font-bold text-lg md:text-2xl text-foreground-950">{value}</span>
      <span className="text-xs md:text-sm text-foreground-500 mt-0.5">{label}</span>
    </div>
  );
}